// IEA Site Survey sending. Words: IEA_OFFLINE_SHELL_WORDS.md rev 3, sections 10 to 13.
// Every save goes to the device list first, then to the cloud. A record is Sent only when the
// cloud confirms it. No signal never refuses anything: it just waits. Only a refusal from the
// cloud stops retries, and Upload All tries those again.
// Photos go after their record is sent, unless Hold Until Upload All is on.

import {
  cloudRecordedAt,
  withTimeLimit,
  errorCode,
  sendSurveyRecord,
  uploadDrivePdf,
  uploadSurveyPhoto,
  writeWalkStatus,
} from "./firebase";
import { isOnline } from "./offline";
import { backupFileName, backupWins, BACKUP_KIND, checkBackup, deviceList, isDrivePdf, refusedWords, type Backup, type PhotoEntry, type RecordEntry, type WalkStatusDoc } from "./outbox";
import type { SignInClaims } from "./badge";

const PHOTO_KEY = "st_survey_photos";
const DEVICE_ID_KEY = "st_survey_device_id";

function ls(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function photosHeld(): boolean {
  return ls()?.getItem(PHOTO_KEY) === "hold";
}

export function setPhotosHeld(hold: boolean): void {
  try {
    ls()?.setItem(PHOTO_KEY, hold ? "hold" : "auto");
  } catch {
    // Not remembered. Photos keep the default.
  }
}

/** A random ID for this device, so another device's survey shows apart from this one. */
export function deviceId(): string {
  let id = ls()?.getItem(DEVICE_ID_KEY) ?? "";
  if (!id) {
    id = `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    try {
      ls()?.setItem(DEVICE_ID_KEY, id);
    } catch {
      // A fresh ID each open. Survey in progress may show this device as another one.
    }
  }
  return id;
}

let sending = false;
let listeners: (() => void)[] = [];

/** The page redraws its strip and Outbox when anything changes. */
export function onOutboxChange(fn: () => void): void {
  listeners.push(fn);
}

function changed(): void {
  for (const fn of listeners) fn();
}

/** Step 3 cards call this. The record waits on the device before anything else happens. */
export async function saveRecord(entry: Omit<RecordEntry, "state" | "reason" | "sentAt">): Promise<void> {
  await deviceList.putRecord({ ...entry, state: "waiting", reason: "", sentAt: "" });
  changed();
  void sendWaiting(false);
}

/** Step 3 cards call this for each photo, after the record is saved. */
export async function savePhoto(entry: Omit<PhotoEntry, "state" | "reason">): Promise<void> {
  await deviceList.putPhoto({ ...entry, state: "waiting", reason: "" });
  changed();
  void sendWaiting(false);
}

/** How long one save waits for the cloud before the pass moves on. It stays waiting and goes again next pass. */
export const SEND_WAIT_MS = 60_000;

async function sendOne(r: RecordEntry): Promise<void> {
  try {
    await withTimeLimit(sendSurveyRecord(r.path, r.data), SEND_WAIT_MS);
    await deviceList.putRecord({ ...r, state: "sent", reason: "", sentAt: new Date().toISOString() });
    await deviceList.addLog(`Sent ${r.assetId} · ${r.card}`);
  } catch (e) {
    const words = refusedWords(errorCode(e));
    if (words) {
      await deviceList.putRecord({ ...r, state: "refused", reason: words });
      await deviceList.addLog(`Refused ${r.assetId} · ${r.card}`);
    } else {
      // Not a refusal: it stays waiting. The Sync Log says what the cloud answered, so a stuck save always shows why (2026-10-22).
      await deviceList.addLog(notSentWords(r, e)).catch(() => undefined);
    }
  }
}

/** The Sync Log line for a save that did not send and was not refused. */
export function notSentWords(r: Pick<RecordEntry, "assetId" | "card">, e: unknown): string {
  const code = errorCode(e);
  const msg = e instanceof Error ? e.message : String(e);
  return `Not sent yet ${r.assetId} · ${r.card}: ${code || msg}`;
}

async function uploadOne(p: PhotoEntry): Promise<void> {
  if (isDrivePdf(p)) return uploadDriveOne(p);
  try {
    await uploadSurveyPhoto(p.path, p.blob, p.contentType);
    await deviceList.putPhoto({ ...p, state: "sent", reason: "" });
    await deviceList.addLog(`Photo uploaded ${p.path.split("/").pop()}`);
  } catch (e) {
    const code = errorCode(e);
    if (code === "unauthorized") {
      await deviceList.putPhoto({ ...p, state: "refused", reason: "Refused: no IEA Site Survey switch, or a photo with this name is already in the cloud." });
    }
  }
}

/** A label PDF for Drive (SITE_FOLDERS_WORDS.md section 12). The server copies it into the site's folder. */
async function uploadDriveOne(p: PhotoEntry): Promise<void> {
  // A Field Labels room or gear photo rides the same list (ROOM_GEAR_PHOTO_B5_WORDS.md).
  const isPhoto = p.path.includes("/driveOutbox/photo/");
  try {
    await uploadDrivePdf(p.path, p.blob, p.meta ?? {}, p.contentType || "application/pdf");
    await deviceList.putPhoto({ ...p, state: "sent", reason: "" });
    await deviceList.addLog(`${isPhoto ? "Photo" : "Label PDF"} sent for Drive ${p.path.split("/").pop()}`);
  } catch (e) {
    if (errorCode(e) === "unauthorized") {
      await deviceList.putPhoto({
        ...p,
        state: "refused",
        reason: isPhoto
          ? "Refused: your badge does not cover this site, or this photo is already in the cloud. Retake it to send a new one."
          : "Refused: a label PDF with this name is already in the cloud, or your badge does not cover this site. Print again to save a new copy.",
      });
    }
  }
}

/** The Print QR Labels sheet calls this. The PDF waits on the iPad like a photo and sends on signal. */
export async function saveDrivePdf(entry: Omit<PhotoEntry, "state" | "reason" | "recordPath">): Promise<void> {
  await deviceList.putPhoto({ ...entry, recordPath: "", state: "waiting", reason: "" });
  changed();
  void sendWaiting(false);
}

/**
 * Sends what waits. Background passes skip refused records and held photos.
 * Upload All (all = true) tries everything again.
 */
export async function sendWaiting(all: boolean): Promise<void> {
  if (sending || !isOnline()) return;
  sending = true;
  try {
    // Records one at a time: each resolves only when the cloud confirms.
    // Saves made while a pass runs (a run of Model Tags) are picked up before the pass ends.
    const tried = new Set<string>();
    for (;;) {
      const records = await deviceList.records();
      const todo = records.filter((r) => !tried.has(`${r.path}|${r.savedAt}`) && (r.state === "waiting" || (all && r.state === "refused")));
      if (!todo.length) break;
      for (const r of todo) {
        tried.add(`${r.path}|${r.savedAt}`);
        await sendOne(r);
      }
    }
    changed();
    const sentPaths = new Set((await deviceList.records()).filter((r) => r.state === "sent").map((r) => r.path));
    const allPhotos = await deviceList.photos();
    // Label PDFs for Drive: no record to wait on, never held by Hold Until Upload All.
    for (const p of allPhotos.filter((x) => isDrivePdf(x) && (x.state === "waiting" || (all && x.state === "refused")))) await uploadOne(p);
    if (all || !photosHeld()) {
      const photos = allPhotos.filter((p) => !isDrivePdf(p) && (p.state === "waiting" || (all && p.state === "refused")) && sentPaths.has(p.recordPath));
      for (const p of photos) await uploadOne(p);
    }
  } finally {
    sending = false;
    changed();
  }
}

/** Survey in progress, written for this device. Sends when it can; never blocks the page. */
export function markSurvey(badge: SignInClaims, siteNumber: string, deviceName: string, state: "Open" | "Ended", waiting: number, startedAt: string): void {
  const now = new Date().toISOString();
  const status: WalkStatusDoc = {
    "Site Number": siteNumber,
    "Badge ID": badge.badgeId,
    "Holder Name": badge.holderName,
    "Device ID": deviceId(),
    "Device Name": deviceName,
    State: state,
    "Started At": startedAt || now,
    "Last Seen": now,
    Waiting: waiting,
  };
  void writeWalkStatus(badge.company, siteNumber, status).catch(() => undefined);
}

// ---- Backup ----

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

function blobToBase64(b: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
    r.onerror = () => reject(r.error);
    r.readAsDataURL(b);
  });
}

function base64ToBlob(b64: string, type: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}

/** Every waiting record and photo in one file, saved to Files (or downloaded on a desktop). */
export async function exportBackup(company: string, siteName: string, deviceName: string): Promise<string> {
  const records = (await deviceList.records()).filter((r) => r.state !== "sent" && r.company === company);
  const photos = (await deviceList.photos()).filter((p) => p.state !== "sent" && p.company === company);
  const backup: Backup = {
    kind: BACKUP_KIND,
    version: 1,
    company,
    exportedAt: new Date().toISOString(),
    deviceName,
    records,
    photos: await Promise.all(photos.map(async ({ blob, ...rest }) => ({ ...rest, base64: await blobToBase64(blob) }))),
  };
  const name = backupFileName(siteName, backup.exportedAt);
  const file = new File([JSON.stringify(backup)], name, { type: "application/json" });
  const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
  if (nav.canShare?.({ files: [file] })) {
    await navigator.share({ files: [file], title: name });
  } else {
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
  await deviceList.addLog(`Backup made: ${plural(records.length, "record")}, ${plural(photos.length, "photo")}`);
  changed();
  return `Backup saved: ${name}. ${plural(records.length, "record")}, ${plural(photos.length, "photo")}.`;
}

/**
 * Sends what a backup holds. Needs signal. A record already sent from this device is skipped.
 * A record the cloud holds a newer copy of is skipped. Photos follow their records.
 */
export async function loadBackup(file: File, badge: SignInClaims): Promise<string> {
  if (!isOnline()) throw new Error("Load Backup needs signal.");
  let raw: unknown;
  try {
    raw = JSON.parse(await file.text());
  } catch {
    throw new Error("That file is not a SafeTag survey backup.");
  }
  const check = checkBackup(raw, badge.company, badge.sites);
  if (!check.ok) throw new Error(check.error);
  const mine = new Map((await deviceList.records()).map((r) => [r.path, r]));
  let sent = 0;
  let skipped = 0;
  for (const r of check.backup.records) {
    if (mine.get(r.path)?.state === "sent") {
      skipped++;
      continue;
    }
    const at = String(r.data["Recorded At"] ?? r.savedAt);
    if (!backupWins(await cloudRecordedAt(r.path).catch(() => null), at)) {
      skipped++;
      continue;
    }
    await deviceList.putRecord({ ...r, state: "waiting", reason: "" });
    sent++;
  }
  const myPhotos = new Map((await deviceList.photos()).map((p) => [p.path, p]));
  for (const p of check.backup.photos) {
    if (myPhotos.get(p.path)?.state === "sent") continue;
    const { base64, ...rest } = p;
    await deviceList.putPhoto({ ...rest, blob: base64ToBlob(base64, p.contentType), state: "waiting", reason: "" });
  }
  await deviceList.addLog(`Backup loaded: ${sent} to send, ${skipped} already in the cloud`);
  changed();
  await sendWaiting(true);
  return `Backup loaded. ${sent} sent, ${skipped} already in the cloud.`;
}

/** What the Outbox and strip show. */
export async function outboxNow(): Promise<{ records: RecordEntry[]; photos: PhotoEntry[] }> {
  try {
    return { records: await deviceList.records(), photos: await deviceList.photos() };
  } catch {
    return { records: [], photos: [] };
  }
}
